import { Flex, Image, Text, Box, Badge } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@chakra-ui/icons';
import { IconType } from 'react-icons';
import { NewChatButton } from 'src/fusion-copilot/components/NewChatButton';

interface AccordionTabProps {
	icon: string | IconType;
	title: string;
	isExpanded: boolean;
	onClick: () => void;
	color?: string;
	onClose?: () => void;
	showNewChatButton?: boolean; // Control visibility of "Start new chat" button
	badgeCount?: number; // Optional badge count (e.g., unread messages)
}

/**
 * AccordionTab Component
 *
 * IMPORTANT: This serves as the header for panels in the right sidebar (DESKTOP only)
 * For Fusion Copilot on mobile, see Header component in fusion-copilot/components/header.tsx
 */
export const AccordionTab = ({
	icon,
	title,
	isExpanded,
	onClick,
	color,
	onClose,
	showNewChatButton = false,
	badgeCount = 0,
}: AccordionTabProps) => {
	const renderIcon = () => {
		if (typeof icon === 'string') {
			return <Image src={icon} w={5} mr={2} />;
		}
		const IconComponent = icon as React.ComponentType<{ size?: number }>;
		return (
			<Box mr={2} display="flex" alignItems="center" position="relative">
				<IconComponent size={20} />
				{badgeCount > 0 && (
					<Badge
						position="absolute"
						top="-6px"
						right="-10px"
						borderRadius="full"
						bg="red.500"
						color="white"
						fontSize="9px"
						minW="16px"
						h="16px"
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight="bold"
					>
						{badgeCount > 9 ? '9+' : badgeCount}
					</Badge>
				)}
			</Box>
		);
	};

	return (
		<Flex
			align="center"
			px={1}
			pl={4}
			py={3}
			bg={isExpanded ? 'gray.100' : 'white'}
			borderBottom="1px solid #eee"
			cursor="pointer"
			minH="48px"
			onClick={onClick}
		>
			{renderIcon()}
			<Text color={color} fontWeight={isExpanded ? 'bold' : 'normal'} flex={1}>
				{title}
			</Text>

			{/* Reusable "Start new chat" button for Fusion Copilot */}
			{showNewChatButton && isExpanded && (
				<Box mr={1} onClick={(e) => e.stopPropagation()}>
					<NewChatButton size="sm" variant="solid" />
				</Box>
			)}

			<Box cursor="pointer" mt={0.5}>
				{isExpanded ? (
					<ChevronUpIcon boxSize={6} />
				) : (
					<ChevronDownIcon boxSize={6} />
				)}
			</Box>

			<Box
				px={3}
				onClick={(e) => {
					e.stopPropagation();
					onClose?.();
				}}
				cursor="pointer"
			>
				<CloseIcon aria-label="Close right sidebar" boxSize={2.5} />
			</Box>
		</Flex>
	);
};
